/** A boolean as a shape and words: a check or a dash, and what it means (DESIGN.md
 *  Icon-And-Words). In a column whose header names the flag the words may be empty — the
 *  state is then still read out ("on" / "off").
 *
 *  Shared by the record editor's draft tables (`records/fields/DraftCell.tsx`) and the
 *  tag-category associations table. */
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import { Box, Typography } from '@mui/material';

export function BoolText({
  on,
  onText = 'Yes',
  offText = 'No',
  muted,
}: {
  on: boolean;
  onText?: string;
  offText?: string;
  muted?: boolean;
}) {
  const text = on ? onText : offText;
  return (
    <Box
      component="span"
      data-bool={String(on)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        color: muted || !on ? 'text.secondary' : 'text.primary',
      }}
    >
      {on ? (
        <CheckIcon
          aria-hidden
          sx={{ fontSize: 16, color: muted ? 'text.secondary' : 'primary.main' }}
        />
      ) : (
        <RemoveIcon aria-hidden sx={{ fontSize: 16 }} />
      )}
      {text ? (
        <Typography component="span" variant="body2" sx={{ color: 'inherit' }}>
          {text}
        </Typography>
      ) : (
        <Box component="span" sx={visuallyHidden}>
          {on ? 'on' : 'off'}
        </Box>
      )}
    </Box>
  );
}

/** Off screen, still read out. px strings: in sx a bare `1` is 100%. */
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  m: '-1px',
  p: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
